/**
 * Orchestrator for the public event viewer — `/event/<id>` (Step 15 →
 * MVP three-mode rebuild per handover §3).
 *
 * ONE layout, three modes, URL state `?mode=browse|detail|theater&car=
 * <device_id>`:
 *   browse   full-bleed map + right panel (Cars tab)
 *   detail   map shrunk by the rail width + Driver tab with video
 *   theater  full-bleed video + rail with LOCAL tab switching
 *
 * This file owns every piece of cross-mode state and behavior — data
 * hooks, selection, lap/compare/seek plumbing, stream-health lag
 * derivation — and threads it to components/viewer/* via the
 * ViewerController bundle. Rendering lives in:
 *   viewer/ViewerModeRouter   mode router + shared map/panel shell
 *   viewer/{Browse,Detail,Theater}Mode, RightPanel, CarsTab, DriverTab,
 *   LapSelector, StageEvents, MapCanvas, MiniMap, FeedPlayer, …
 *
 * Preserved load-bearing behavior (verify against git history before
 * touching):
 *   - replay mode for ended events (useEventLiveDevices replay branch)
 *   - useEventRuns/useEventLaps incl. the lap_completed SSE merge
 *   - lap click → seek via lapOverride + KEYED FeedPlayer remount (M21:
 *     key on session_id, never reuse a player across sessions)
 *   - SSE session_id null is authoritative (M19 — useEventLiveDevices)
 *   - seekToUtc / initialSeekUtcMs plumbing, σ/confidence chips,
 *     best-lap badge, EventHeader + FilterStrip
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { EventRow, Lap, Run } from '../services/fleet';
import { EventLiveDevice } from '../hooks/useEventLiveDevices';
import { DeviceTrailTarget, RacelineTrimContext, useDeviceTrail } from '../hooks/useDeviceTrail';
import { mergeGpsTracks, useEventDeviceGps } from '../hooks/useEventDeviceGps';
import { useEventEnrollments } from '../hooks/useEventEnrollments';
import { useEventLaps } from '../hooks/useEventLaps';
import { useEventRaceline } from '../hooks/useEventRaceline';
import { useEventRuns } from '../hooks/useEventRuns';
import { useEventStageRoutes } from '../hooks/useEventStageRoutes';
import { useStageSessions } from '../hooks/useStageSessions';
import { buildStageViews, stageLabel } from '../pages/admin/stages/stageModel';
import { LG_MEDIA_QUERY, useMediaQuery } from '../hooks/useMediaQuery';
import { computeCompareSeed } from '../lib/crossTrajectory';
import { nearestPointOnPolyline } from '../plugins/synced_viewer/util';
import { GlassRailData, HLSPlayerHandle } from './HLSPlayer';
import CompareRunPicker from './CompareRunPicker';
import EventHeader from './viewer/EventHeader';
import ViewerModeRouter, { ViewerController } from './viewer/ViewerModeRouter';
import Landing from './viewer/Landing';
import { displayCarNumber } from './viewer/carNumber';
import { CarsDensity } from './viewer/CarsTab';
import { CompareState } from './viewer/DriverTab';
import { Filter } from './viewer/FilterStrip';
import { DriverSessionRow } from './viewer/SessionSelector';
import StageStrip, { StageOption } from './viewer/StageStrip';
import { clearResumePosition, loadResumePosition, saveResumePosition } from './viewer/resumePosition';
import { MapModules, loadMapModules } from './viewer/MapCanvas';
import { VideoBadge } from './viewer/FeedPlayer';
import { useStageEvents } from './viewer/StageEvents';
import { useEventStandings } from './viewer/useEventStandings';
import { LagInfo, lagFromPlayer, lagFromTelemetryAge, useNowTick } from './viewer/lag';
import { useViewerUrlState } from './viewer/urlState';

interface BrowseStateViewProps {
  event: EventRow;
  devices: EventLiveDevice[];
  /** Event window has passed — roster is recorded runs, players are VOD. */
  replay?: boolean;
}

/** Cars-list density — persisted, shared across every mode's CarsTab. */
const DENSITY_KEY = 'tuvion-cars-density';

export const BrowseStateView: React.FC<BrowseStateViewProps> = ({
  event,
  devices,
  replay = false,
}) => {
  const [modules, setModules] = useState<MapModules | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [playheadUtc, setPlayheadUtc] = useState<number | null>(null);
  const playerRef = useRef<HLSPlayerHandle | null>(null);
  const lastPlayheadTickRef = useRef(0);

  // ── URL-backed mode + car selection ───────────────────────────────────
  const { mode, carId, stageIndex, setMode, selectCar, setStageIndex } = useViewerUrlState();
  const selected = useMemo(
    () => devices.find((d) => d.device_id === carId) ?? null,
    [devices, carId],
  );
  const selectedId = selected?.device_id ?? null;

  // ── Stage selector ──────────────────────────
  // Chips from the same stages_json ⨯ gates merge the admin uses; only
  // multi-stage events get the strip. A `?stage=` pointing at an index
  // that doesn't exist still counts as selected (harmless: no sessions).
  const stageOptions = useMemo<StageOption[]>(
    () =>
      buildStageViews(event).map((v) => ({
        index: v.index,
        label: stageLabel(v.index, v.type),
        name: v.name,
        startTime: v.startTime,
      })),
    [event],
  );
  const stageStripVisible = stageOptions.length >= 2;
  const selectedStage = stageStripVisible ? stageIndex : null;
  // Stage-scoped sessions (public endpoint; 30 s poll). Gated off
  // entirely while All is selected.
  const { sessions: stageSessions } = useStageSessions(
    selectedStage != null ? event.id : null,
    selectedStage,
  );
  // device_id → the session to play for the selected stage: newest
  // ATTRIBUTED session (stage_index === selected, M3), falling back to
  // the newest same-date unattributed one (legacy continuity — matches
  // the admin stage-sessions card semantics).
  const stageSessionByDevice = useMemo<Map<string, string>>(() => {
    const out = new Map<string, string>();
    if (selectedStage == null) return out;
    const best = new Map<string, { attributed: boolean; startMs: number; id: string }>();
    for (const s of stageSessions) {
      const attributed = s.stage_index === selectedStage;
      const startMs = s.start_utc_ms ?? 0;
      const cur = best.get(s.device_id);
      if (
        cur == null ||
        (attributed && !cur.attributed) ||
        (attributed === cur.attributed && startMs > cur.startMs)
      ) {
        best.set(s.device_id, { attributed, startMs, id: s.session_id });
      }
    }
    for (const [dev, b] of best) out.set(dev, b.id);
    return out;
  }, [stageSessions, selectedStage]);
  const stageSessionId =
    selectedStage != null && selectedId != null
      ? stageSessionByDevice.get(selectedId) ?? null
      : null;
  const selectedStageLabel =
    selectedStage != null
      ? stageOptions.find((o) => o.index === selectedStage)?.label ?? `SS${selectedStage + 1}`
      : null;

  const goBrowse = useCallback(() => setMode('browse'), [setMode]);
  const goDetail = useCallback(() => setMode('detail'), [setMode]);
  const goTheater = useCallback(() => setMode('theater'), [setMode]);

  useEffect(() => {
    let cancelled = false;
    loadMapModules()
      .then((m) => {
        if (!cancelled) setModules(m);
      })
      .catch((err) => console.warn('[viewer] leaflet load failed', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Cars-list density (localStorage, shared across modes) ─────────────
  const [density, setDensityState] = useState<CarsDensity>(() => {
    try {
      const raw = localStorage.getItem(DENSITY_KEY);
      if (raw === 'list' || raw === 'preview') return raw;
    } catch {
      /* ignore */
    }
    return 'preview';
  });
  const setDensity = useCallback((d: CarsDensity) => {
    setDensityState(d);
    try {
      localStorage.setItem(DENSITY_KEY, d);
    } catch {
      /* ignore */
    }
  }, []);

  // Throttle to ~10 Hz so the moving arrow doesn't drive 60Hz re-renders.
  const handlePlayheadUtc = useCallback((utcMs: number | null) => {
    const now =
      typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
    if (utcMs == null) {
      lastPlayheadTickRef.current = now;
      setPlayheadUtc(null);
      return;
    }
    if (now - lastPlayheadTickRef.current < 100) return;
    lastPlayheadTickRef.current = now;
    setPlayheadUtc(utcMs);
  }, []);

  const handleSeek = useCallback((utcMs: number) => {
    playerRef.current?.seekToUtc(utcMs);
  }, []);

  // In replay mode session_id points at each device's most recent
  // recording (for the VOD player), not a live feed — nothing is live.
  const liveCount = useMemo(
    () => (replay ? 0 : devices.filter((d) => d.session_id != null).length),
    [devices, replay],
  );

  // Landing (handover §3.6): pre-live → countdown card; live window
  // OPEN but zero armed phones → 'quiet' card (a blank map + tiny
  // "no armed phones yet" was a dead end for early fans — the common
  // case, since crews arm only shortly before their stage). Both are
  // dismissable to browse anyway; the quiet screen auto-yields the
  // moment a device arms (devices.length > 0).
  const [landingDismissed, setLandingDismissed] = useState(false);
  // Countdown expiry re-renders us into the quiet variant (nothing
  // else re-renders a pre-live page with no armed devices).
  const [preLiveExpired, setPreLiveExpired] = useState(false);
  const preLive = !preLiveExpired && Date.now() < new Date(event.active_from).getTime();
  const showLanding =
    !replay &&
    !landingDismissed &&
    liveCount === 0 &&
    (preLive || devices.length === 0);
  const landingVariant: 'prelive' | 'quiet' = preLive ? 'prelive' : 'quiet';
  const isDesktop = useMediaQuery(LG_MEDIA_QUERY);
  const showEventHeader = showLanding || isDesktop;

  // Followed cars — persisted per event so a refresh keeps the set.
  const followKey = `yelve.viewer.follow.${event.id}`;
  const [followedIds, setFollowedIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(followKey);
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  });
  const toggleFollow = useCallback(
    (id: string) => {
      setFollowedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        try {
          localStorage.setItem(followKey, JSON.stringify([...next]));
        } catch {
          // storage full/blocked — follow still works for the session
        }
        return next;
      });
    },
    [followKey],
  );

  const filtered = useMemo(() => {
    if (filter === 'live') {
      return replay ? [] : devices.filter((d) => d.session_id != null);
    }
    if (filter === 'following') {
      return devices.filter((d) => followedIds.has(d.device_id));
    }
    return devices;
  }, [devices, filter, replay, followedIds]);

  // Raceline + runs. Both gate cleanly on absence (no toggle, no upload,
  // or zero runs) and the viewer falls back to v1 behaviour.
  const { raceline } = useEventRaceline(event.id, event.show_raceline);
  const { runs, findRunForSession } = useEventRuns(event.id);
  const enrollments = useEventEnrollments(event.id);
  // W8 — official corridor geometry for every stage, drawn on the map.
  const { routes: stageRoutes } = useEventStageRoutes(event.id);

  // Most recent recording for the selected device. Open-ended events
  // never flip the page to replay mode, so an idle device used to show
  // a black "not live right now" box with no trail — even though its
  // runs are sitting right there. This is the VOD fallback: when the
  // device isn't live (and no lap override is mounted), play its last
  // recording and draw its trail. Runs arrive newest-first.
  //
  // Pick the newest run that actually HAS playable footage, and within it
  // the last playable session. A stray unplayable session (e.g. an aborted
  // recording that never produced media) groups into its own newest run
  // and would otherwise shadow the real footage — resolving the device to
  // an empty session that plays nothing.
  const lastRunSessionId = useMemo<string | null>(() => {
    if (!selected) return null;
    const deviceRuns = runs.filter((r) => r.device_id === selected.device_id);
    for (const run of deviceRuns) {
      const withFootage = run.sessions.filter((s) => s.has_media);
      if (withFootage.length > 0) {
        return withFootage[withFootage.length - 1].session_id;
      }
    }
    // No run has a known-playable session — fall back to the newest run's
    // last session so we never regress to null when playability is unknown.
    const last = deviceRuns[0]?.sessions[deviceRuns[0].sessions.length - 1];
    return last?.session_id ?? null;
  }, [selected, runs]);

  // Circuit lap list for the selected competitor. Gates on event type;
  // live pages also merge lap_completed SSE frames.
  const isCircuit = (event.type ?? '').trim().toLowerCase() === 'circuit';
  const { laps } = useEventLaps(
    isCircuit ? event.id : null,
    selectedId,
    !replay,
  );
  // Best-lap standings → Cars-list Pos/Gap cells (circuit only).
  const standings = useEventStandings(event.id, isCircuit, !replay);
  // Clicking a lap from a session other than the mounted one remounts
  // the FeedPlayer on that session with a one-shot initial seek.
  const [lapOverride, setLapOverride] = useState<{
    sessionId: string;
    utcMs: number;
  } | null>(null);
  // Index into `laps` of the selected PAST lap (drives chip highlight,
  // the REPLAY · Ln badge and the All-chip echo); null = live/none.
  const [selectedLapIdx, setSelectedLapIdx] = useState<number | null>(null);
  // Explicit SessionSelector pick (session surfacer). Sits between
  // lapOverride (a pick WITH a seek) and the stage/live/last fallbacks.
  const [pickedSessionId, setPickedSessionId] = useState<string | null>(null);

  const handlePickLap = useCallback(
    (lap: Lap, idx: number) => {
      setSelectedLapIdx(idx);
      const mountedSession =
        lapOverride?.sessionId ??
        pickedSessionId ??
        (selectedStage != null
          ? stageSessionId ?? selected?.session_id ?? null
          : selected?.session_id ?? lastRunSessionId);
      if (lap.session_id === mountedSession) {
        playerRef.current?.seekToUtc(lap.start_utc_ms);
      } else {
        setLapOverride({ sessionId: lap.session_id, utcMs: lap.start_utc_ms });
      }
    },
    [lapOverride, pickedSessionId, selected, lastRunSessionId, selectedStage, stageSessionId],
  );

  // Forces a fresh player attach (→ live edge) when the user picks the
  // live chip after an IN-SESSION lap seek: lapOverride is null in that
  // path, so the keyed remount wouldn't otherwise fire and the player
  // would keep playing the old lap while badged LIVE (review major).
  const [liveEpoch, setLiveEpoch] = useState(0);

  // Live chip: restore the LIVE badge; if a foreign session was mounted
  // via lapOverride, drop it so the live session remounts (keyed, M21).
  const handlePickLive = useCallback(() => {
    if (
      lapOverride == null &&
      pickedSessionId == null &&
      selectedLapIdx != null &&
      selected?.session_id
    ) {
      // The user explicitly chose live over the lap they just seeked to;
      // forget that saved spot so the keyed live remount below doesn't
      // resume straight back into it while badged LIVE (review P1).
      clearResumePosition(selected.session_id);
      setLiveEpoch((n) => n + 1);
    }
    setSelectedLapIdx(null);
    setLapOverride(null);
    setPickedSessionId(null);
  }, [lapOverride, pickedSessionId, selectedLapIdx, selected]);

  // Per-car rows for the SessionSelector, newest first. Runs arrive
  // newest-first with chronological sessions inside; skip sessions
  // KNOWN to have no playable media (aborted recordings) unless live.
  const driverSessions = useMemo<DriverSessionRow[]>(() => {
    if (!selected) return [];
    const rows: DriverSessionRow[] = [];
    for (const run of runs) {
      if (run.device_id !== selected.device_id) continue;
      for (const s of run.sessions) {
        const isLive = !replay && s.session_id === selected.session_id;
        if (!isLive && !s.has_media) continue;
        rows.push({
          sessionId: s.session_id,
          startUtcMs: s.start_utc_ms,
          endUtcMs: s.end_utc_ms,
          segmentCount: s.segment_count,
          stageTag:
            s.stage_index != null
              ? stageOptions.find((o) => o.index === s.stage_index)?.label ??
                `SS${s.stage_index + 1}`
              : null,
          isLive,
        });
      }
    }
    rows.sort((a, b) => (b.startUtcMs ?? 0) - (a.startUtcMs ?? 0));
    return rows;
  }, [selected, runs, replay, stageOptions]);

  // Row tap: mount that session (keyed remount, resume position applies);
  // the LIVE row routes through handlePickLive so the back-to-live
  // remount/epoch invariants hold.
  const handlePickSession = useCallback(
    (sessionId: string) => {
      if (!replay && sessionId === selected?.session_id) {
        handlePickLive();
        return;
      }
      setSelectedLapIdx(null);
      setLapOverride(null);
      setPickedSessionId(sessionId);
    },
    [replay, selected, handlePickLive],
  );

  const [comparePickerOpen, setComparePickerOpen] = useState(false);
  const [compareBusy, setCompareBusy] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [compareRun, setCompareRun] = useState<CompareState | null>(null);
  const compareePlayerRef = useRef<HLSPlayerHandle | null>(null);

  // The session whose footage is (or would be) on the stage right now:
  // an explicit lap override wins, then the live session, then the
  // idle-device VOD fallback. Trail, mini-map playhead dot and
  // click-to-seek all key off THIS — not the live session id — so a
  // lap replay on an idle device draws its trace (field bug: selected
  // driver showed no trace and the circuit mini-map lost its GPS dot).
  //
  // With a STAGE selected the chain changes: that stage's attributed
  // session wins over live (the user asked for SS<n>, not whatever the
  // car streams now — and a car live ON the stage resolves to the same
  // id, so live still plays as live). A car with NO footage on the
  // stage falls back to its live feed or nothing — never the generic
  // last-recording fallback, which could silently be another stage's
  // footage.
  // An explicit SessionSelector pick slots under lapOverride (a pick
  // WITH a seek) and above the stage/live/last-recording fallbacks.
  const playbackSessionId =
    lapOverride?.sessionId ??
    pickedSessionId ??
    (selectedStage != null
      ? stageSessionId ?? selected?.session_id ?? null
      : selected?.session_id ?? lastRunSessionId);

  // Resolve the playing session to its stitched run; a session that
  // isn't in the runs index yet (very fresh recording) falls back to a
  // single-session synthetic run so v1 behaviour holds during the
  // first refresh window.
  const trailTarget = useMemo<DeviceTrailTarget | null>(() => {
    if (!playbackSessionId) return null;
    const run = findRunForSession(playbackSessionId);
    if (run) return { sessions: run.sessions.map((s) => s.session_id) };
    return { sessions: [playbackSessionId] };
  }, [playbackSessionId, findRunForSession]);

  const racelineCtx = useMemo<RacelineTrimContext | null>(() => {
    if (!raceline) return null;
    return {
      coords: raceline.coords,
      cumulativeS: raceline.cumulativeS,
      totalLengthM: raceline.totalLengthM,
    };
  }, [raceline]);

  // GPS trail + video coverage for the currently-selected pin. Hook
  // gates on session_id; pins without a recording session render no
  // overlay. When a raceline is present, the trail is trimmed to its
  // window so off-stage transit doesn't pollute the polyline.
  const trail = useDeviceTrail(trailTarget, racelineCtx);

  // W8 — event-wide competitor path (including liaison GPS received
  // between recordings), merged with the session trail so the "All
  // stages" map view shows the whole route, not just the mounted
  // session's span. Only fetched with no stage selected — a specific
  // stage's map stays session-scoped.
  const eventGps = useEventDeviceGps(event.id, selectedId, selectedStage == null);
  const mapTrailTrack = useMemo(
    () => (selectedStage == null ? mergeGpsTracks(eventGps.track, trail.track) : trail.track),
    [eventGps.track, selectedStage, trail.track],
  );

  // Stage-progress chip: project the currently-playing GPS sample onto
  // the raceline to read its s-coordinate. Updates at playhead-tick
  // cadence (10 Hz) since playheadUtc itself is already throttled.
  const stageProgress = useMemo<{ pct: number; sM: number } | null>(() => {
    if (!raceline || playheadUtc == null) return null;
    const t = trail.track;
    if (t.length === 0) return null;
    // Find the GPS sample whose UTC is closest to the playhead.
    let bestIdx = 0;
    let bestDelta = Math.abs(t[0][2] - playheadUtc);
    for (let i = 1; i < t.length; i += 1) {
      const d = Math.abs(t[i][2] - playheadUtc);
      if (d < bestDelta) {
        bestDelta = d;
        bestIdx = i;
      }
    }
    const [lat, lon] = t[bestIdx];
    const proj = nearestPointOnPolyline(raceline.coords, raceline.cumulativeS, lat, lon);
    if (!proj) return null;
    if (proj.s_m < 0 || proj.s_m > raceline.totalLengthM) {
      return { pct: -1, sM: proj.s_m };
    }
    return {
      pct: raceline.totalLengthM > 0 ? (proj.s_m / raceline.totalLengthM) * 100 : 0,
      sM: proj.s_m,
    };
  }, [raceline, playheadUtc, trail.track]);

  // Reset playhead between selections so a stale UTC doesn't ghost the
  // arrow into the new track's range. Also tear down any open Compare
  // — switching cars means the s-anchor + secondary feed no longer
  // belong with the new primary — and the lap selection. A stage-chip
  // switch is a selection change too (different session mounts).
  useEffect(() => {
    setPlayheadUtc(null);
    lastPlayheadTickRef.current = 0;
    setCompareRun(null);
    setComparePickerOpen(false);
    setCompareError(null);
    setLapOverride(null);
    setSelectedLapIdx(null);
    setPickedSessionId(null);
  }, [selectedId, selectedStage]);

  const handleCompareOpen = useCallback(() => {
    setCompareError(null);
    setComparePickerOpen(true);
  }, []);

  const handleComparePick = useCallback(
    async (run: Run) => {
      if (!raceline || !trailTarget) return;
      // Anchor s = primary's current sample projection (use playheadUtc
      // if known; otherwise the most recent track sample).
      let anchorLat: number | null = null;
      let anchorLon: number | null = null;
      const t = trail.track;
      if (t.length > 0) {
        const utc = playheadUtc ?? t[t.length - 1][2];
        let bestIdx = 0;
        let bestDelta = Math.abs(t[0][2] - utc);
        for (let i = 1; i < t.length; i += 1) {
          const d = Math.abs(t[i][2] - utc);
          if (d < bestDelta) {
            bestDelta = d;
            bestIdx = i;
          }
        }
        anchorLat = t[bestIdx][0];
        anchorLon = t[bestIdx][1];
      }
      if (anchorLat == null || anchorLon == null) {
        setCompareError('Primary track has no GPS yet — wait a moment.');
        return;
      }
      const anchor = nearestPointOnPolyline(
        raceline.coords,
        raceline.cumulativeS,
        anchorLat,
        anchorLon,
      );
      if (!anchor || anchor.s_m < 0 || anchor.s_m > raceline.totalLengthM) {
        setCompareError('Primary is off the raceline; move to a stage position first.');
        return;
      }
      setCompareBusy(true);
      try {
        const seed = await computeCompareSeed(run, anchor.s_m, {
          coords: raceline.coords,
          cumulativeS: raceline.cumulativeS,
          totalLengthM: raceline.totalLengthM,
        });
        if (!seed) {
          setCompareError("This vehicle never reached the primary's stage position.");
          return;
        }
        setCompareRun({
          runId: seed.runId,
          sessionId: seed.sessionId,
          seedUtcMs: seed.seedUtcMs,
        });
        setComparePickerOpen(false);
      } catch (err) {
        setCompareError(err instanceof Error ? err.message : 'compare failed');
      } finally {
        setCompareBusy(false);
      }
    },
    [raceline, trailTarget, trail.track, playheadUtc],
  );

  const handleCompareClose = useCallback(() => {
    setCompareRun(null);
    setCompareError(null);
  }, []);

  const compareCapable = Boolean(
    raceline && stageProgress && stageProgress.pct >= 0 && trailTarget && runs.length > 1,
  );
  const primaryRunId = useMemo(() => {
    if (!selected?.session_id) return null;
    const run = findRunForSession(selected.session_id);
    return run?.run_id ?? null;
  }, [selected, findRunForSession]);

  const center = useMemo<[number, number]>(() => {
    const fixed = devices.filter((d) => d.has_fix);
    if (fixed.length > 0) {
      let lat = 0;
      let lon = 0;
      for (const d of fixed) {
        lat += d.lat;
        lon += d.lon;
      }
      return [lat / fixed.length, lon / fixed.length];
    }
    // Fall back to the start gate if devices haven't fixed yet.
    const start = event.gates.find((g) => g.kind === 'start');
    if (start) return [start.lat, start.lon];
    const finish = event.gates.find((g) => g.kind === 'finish');
    if (finish) return [finish.lat, finish.lon];
    return [60.1699, 24.9384];
  }, [devices, event]);

  const handleShare = useCallback(async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (navigator.share) {
      try {
        await navigator.share({ title: event.name, url });
      } catch {
        // User cancelled — nothing to do.
      }
      return;
    }
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // ignore clipboard denial
      }
    }
  }, [event.name]);

  // ── Stream-health lag (handover §3) ───────────────────────────────────
  // Source 1: the actively-playing camera reports its VIDEO END (the
  // newest playable media as UTC ms) via HLSPlayer.onVideoEdgeUpdate;
  // lag = newest GPS fix − video end. Both stamps come from the
  // phone's clock, so client skew cancels — and scrubbing back never
  // moves the number (the old playhead-based lag read a 4-minute
  // scrub as "4m" of stream lag; 2026-07-03 field feedback).
  const [playerEdgeUtcMs, setPlayerEdgeUtcMs] = useState<number | null>(null);
  const onPlayerEdge = useCallback((videoEndUtcMs: number | null) => {
    setPlayerEdgeUtcMs(videoEndUtcMs);
  }, []);
  // Player lag is only meaningful while the LIVE session of the selected
  // car is mounted (detail/theater, not a lapOverride VOD, not replay,
  // not a stage-scoped VOD of another session).
  const playerLiveActive =
    mode !== 'browse' &&
    !replay &&
    lapOverride == null &&
    selected?.session_id != null &&
    playbackSessionId === selected.session_id;

  // Source 2: everything else uses last-telemetry age (device SSE
  // freshness) as the proxy, re-derived at 1 Hz so chips age visibly.
  const nowMs = useNowTick(1000);

  // Video-end → GPS-marker lag, seconds. The GPS fix is the preferred
  // "now" (phone clock vs phone clock); wallclock is the fallback when
  // telemetry hasn't produced a fix. Clamped at 0 — a fix briefly
  // older than the video end just means video has caught up to the
  // car's last known position. nowMs keeps the fallback aging at 1 Hz.
  const playerLagSec = useMemo(() => {
    if (playerEdgeUtcMs == null) return null;
    const fixMs = selected?.fix_ts_ms;
    const refMs = fixMs != null ? fixMs : nowMs;
    return Math.max(0, (refMs - playerEdgeUtcMs) / 1000);
  }, [playerEdgeUtcMs, selected?.fix_ts_ms, nowMs]);
  const playerLagInfo: LagInfo | null = playerLiveActive ? lagFromPlayer(playerLagSec) : null;
  const lagFor = useCallback(
    (d: EventLiveDevice): LagInfo => {
      if (replay) {
        // Ended event: nothing streams — muted everywhere.
        return { severity: 'offline', label: d.session_id ? 'vod' : 'idle' };
      }
      if (!d.has_fix) {
        // Armed, possibly recording, but no position yet — the car has
        // a row and no pin, and this is the only place that says why.
        return { severity: 'lagging', label: 'no gps' };
      }
      if (d.session_id == null) {
        // Armed but not recording (or offline) — muted.
        return { severity: 'offline', label: d.online ? 'armed' : 'idle' };
      }
      if (d.device_id === selectedId && playerLiveActive) {
        // Source 1: active player PDT lag.
        return lagFromPlayer(playerLagSec);
      }
      // Source 2: telemetry-age proxy.
      return lagFromTelemetryAge(d.fix_ts_ms, nowMs);
    },
    [replay, selectedId, playerLiveActive, playerLagSec, nowMs],
  );

  // Leaving the player modes: clear the playhead arrow + lag readout so
  // browse doesn't show a frozen value from the unmounted player.
  useEffect(() => {
    if (mode === 'browse') {
      setPlayheadUtc(null);
      setPlayerEdgeUtcMs(null);
    }
  }, [mode]);

  // ── Player mount inputs ───────────────────────────────────────────────
  // playbackSessionId already encodes the precedence (lap override →
  // live → idle-device VOD fallback); M21 keying preserved (session
  // change always remounts); the epoch only adds remounts on the
  // explicit back-to-live action.
  const mountedSessionId = playbackSessionId;
  const mountedPlayerKey = `${mountedSessionId ?? 'no-session'}:${liveEpoch}`;
  // First PDT of the primary player's timeline (v2 only), for the Speed/IMU
  // charts' left edge. Keyed on the mounted SESSION, not the selected car —
  // a lap override or stage switch can change the mounted footage without
  // changing which device is selected, and the old anchor belongs to the
  // footage that's no longer playing.
  const [videoStartMs, setVideoStartMs] = useState<number | null>(null);
  useEffect(() => {
    setVideoStartMs(null);
  }, [mountedSessionId]);
  const handlePdtReady = useCallback((utcMs: number) => {
    setVideoStartMs(utcMs);
  }, []);
  // Drop a stale playhead the instant the mounted session changes — render
  // phase, before the save effect / pagehide flush can fire — so we never
  // persist session A's lingering playhead under session B (the new player
  // re-reports on its first tick). Keeps the saved {session, playhead}
  // pair consistent (review P1).
  const lastMountedSessionRef = useRef<string | null>(mountedSessionId);
  if (lastMountedSessionRef.current !== mountedSessionId) {
    lastMountedSessionRef.current = mountedSessionId;
    if (playheadUtc !== null) {
      setPlayheadUtc(null);
      lastPlayheadTickRef.current = 0;
    }
  }
  // Anything that isn't the device's CURRENT live session plays as VOD
  // — incl. the idle-device fallback recording and a stage-scoped
  // session that isn't the live one.
  const playerVod =
    replay ||
    lapOverride != null ||
    playbackSessionId == null ||
    playbackSessionId !== selected?.session_id;

  // ── Resume playback where the viewer last stopped ─────────────────────
  // Persist the playhead per session (localStorage) and feed it back as
  // the player's initial seek on mount, so re-opening / refreshing a
  // session resumes where you stopped instead of restarting (VOD) or
  // snapping to the live edge. A lap-click seek (lapOverride) still wins.
  // HLSPlayer consumes initialSeekUtcMs exactly once per (keyed) mount and
  // clamps out-of-range targets, so a live buffer that has rolled past the
  // saved point lands near live rather than failing.
  const initialSeekUtcMs = useMemo(
    // liveEpoch is a dep so an explicit "go live" (which clears the saved
    // spot in handlePickLive but changes neither lapOverride nor the
    // session) recomputes this to null instead of the stale resume (P1).
    () => lapOverride?.utcMs ?? loadResumePosition(mountedSessionId),
    [lapOverride, mountedSessionId, liveEpoch],
  );
  // Refs so the hide/unmount flush reads the latest values without
  // re-subscribing its listeners on every playhead tick.
  const resumePlayheadRef = useRef<number | null>(null);
  const resumeSessionRef = useRef<string | null>(null);
  resumePlayheadRef.current = playheadUtc;
  resumeSessionRef.current = mountedSessionId;
  // Cheap periodic save while playing (≤ once / 2 s).
  const lastResumeSaveRef = useRef(0);
  useEffect(() => {
    if (playheadUtc == null || mountedSessionId == null) return;
    const now = Date.now();
    if (now - lastResumeSaveRef.current < 2000) return;
    lastResumeSaveRef.current = now;
    saveResumePosition(mountedSessionId, playheadUtc);
  }, [playheadUtc, mountedSessionId]);
  // Flush the exact stop position on tab-hide / navigation / unmount — the
  // 2 s throttle above can otherwise miss the final frame on a refresh.
  useEffect(() => {
    const flush = () => saveResumePosition(resumeSessionRef.current, resumePlayheadRef.current);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      flush();
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  const videoBadge = useMemo<VideoBadge | null>(() => {
    if (selectedLapIdx != null) {
      // Label by lap_number, not array index: lap 0 is the OUT-lap, and
      // its presence would otherwise shift every "REPLAY · Ln" badge.
      const sel = laps[selectedLapIdx];
      const label =
        sel == null
          ? `L${selectedLapIdx + 1}`
          : sel.lap_number === 0
            ? 'OUT'
            : `L${sel.lap_number}`;
      return { text: `REPLAY · ${label}`, kind: 'replay' };
    }
    // Explicit SessionSelector pick — label with its stage tag when
    // attributed. (A pick of the live session clears the pick instead,
    // so this branch is always a VOD.)
    if (pickedSessionId != null && playbackSessionId === pickedSessionId) {
      const row = driverSessions.find((r) => r.sessionId === pickedSessionId);
      return {
        text: row?.stageTag ? `REPLAY · ${row.stageTag}` : 'REPLAY',
        kind: 'replay',
      };
    }
    // Stage-scoped VOD (not the car's live session) — label the stage.
    if (
      selectedStageLabel != null &&
      stageSessionId != null &&
      playbackSessionId === stageSessionId &&
      (replay || stageSessionId !== selected?.session_id)
    ) {
      return { text: `REPLAY · ${selectedStageLabel}`, kind: 'replay' };
    }
    if (selected?.session_id && !replay && playbackSessionId === selected.session_id) {
      return { text: 'LIVE', kind: 'live' };
    }
    if (playbackSessionId) return { text: 'REPLAY', kind: 'replay' };
    return null;
  }, [selectedLapIdx, replay, selected, playbackSessionId, laps, selectedStageLabel, stageSessionId, pickedSessionId, driverSessions]);

  // Stage events: only what the SSE stream already carries (session
  // start/stop transitions + bitrate command updates) — see StageEvents.
  const stageEvents = useStageEvents(selectedId, !replay);

  // Speed at the current PLAYHEAD — the nearest timestamped trail sample
  // (track tuples are [lat, lon, utc, speed_mps]) so REPLAY shows the
  // speed in the footage, not the live SSE value. Falls back to the live
  // device speed only when live (not replay/lap-override) and before the
  // first playhead tick. Shared by the fullscreen glass rail AND the
  // Driver-tab Speed cell so both agree and both track playback.
  const playbackSpeedKmh = useMemo<number | null>(() => {
    if (!selected) return null;
    const t = trail.track;
    if (playheadUtc != null && t.length > 0) {
      let bestIdx = 0;
      let bestDelta = Math.abs(t[0][2] - playheadUtc);
      for (let i = 1; i < t.length; i += 1) {
        const d = Math.abs(t[i][2] - playheadUtc);
        if (d < bestDelta) {
          bestDelta = d;
          bestIdx = i;
        }
      }
      const sampleSpeed = t[bestIdx][3];
      return typeof sampleSpeed === 'number' ? sampleSpeed * 3.6 : null;
    }
    if (!replay && lapOverride == null && selected.speed_mps != null) {
      return selected.speed_mps * 3.6;
    }
    return null;
  }, [selected, trail.track, playheadUtc, replay, lapOverride]);

  // Lap telemetry for the glass rail (circuit only). lapNumber + the
  // running lap time are PLAYHEAD-correlated (replay shows the lap being
  // watched; live shows the in-progress lap); best/last are over the
  // completed laps. `laps` is sorted oldest→newest (useEventLaps).
  const lapTelemetry = useMemo(() => {
    const none = {
      lapNumber: null as number | null,
      runningLapMs: null as number | null,
      bestLapMs: null as number | null,
      lastLapMs: null as number | null,
    };
    if (!isCircuit || laps.length === 0) return none;
    let bestLapMs: number | null = null;
    for (const l of laps) {
      // Exclude the out-lap (lap_number 0) — partial, not a comparable
      // line-to-line lap.
      if (l.lap_number < 1) continue;
      if (l.duration_ms > 0 && (bestLapMs == null || l.duration_ms < bestLapMs)) {
        bestLapMs = l.duration_ms;
      }
    }
    if (playheadUtc == null) {
      // No playhead yet — the rail needs one to pick a lap, so it shows
      // the media-time fallback until the first tick (sub-100ms).
      return none;
    }
    // Inside a completed lap (replay): that lap's number + elapsed; last =
    // the lap immediately before it.
    const inLapIdx = laps.findIndex(
      (l) => l.start_utc_ms <= playheadUtc && playheadUtc < l.end_utc_ms,
    );
    if (inLapIdx >= 0) {
      const cur = laps[inLapIdx];
      const prevMs = inLapIdx > 0 ? laps[inLapIdx - 1].duration_ms : 0;
      return {
        lapNumber: cur.lap_number,
        runningLapMs: Math.max(0, playheadUtc - cur.start_utc_ms),
        bestLapMs,
        lastLapMs: prevMs > 0 ? prevMs : null,
      };
    }
    // Otherwise: the in-progress lap after the most recent completed one
    // (live edge), or before lap 1.
    let lastDoneIdx = -1;
    for (let i = 0; i < laps.length; i += 1) {
      if (laps[i].end_utc_ms <= playheadUtc) lastDoneIdx = i;
    }
    if (lastDoneIdx >= 0) {
      const lc = laps[lastDoneIdx];
      return {
        lapNumber: lc.lap_number + 1,
        runningLapMs: Math.max(0, playheadUtc - lc.end_utc_ms),
        bestLapMs,
        lastLapMs: lc.duration_ms > 0 ? lc.duration_ms : null,
      };
    }
    return { lapNumber: laps[0].lap_number, runningLapMs: null, bestLapMs, lastLapMs: null };
  }, [isCircuit, laps, playheadUtc]);

  // Glass rail data (handover §3.6): playhead speed + circuit lap cells.
  const glassRail = useMemo<GlassRailData | null>(() => {
    if (!selected) return null;
    return { speedKmh: playbackSpeedKmh, ...lapTelemetry };
  }, [selected, playbackSpeedKmh, lapTelemetry]);

  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  const controller: ViewerController = {
    event,
    replay,
    isCircuit,
    mode,
    devices,
    filtered,
    liveCount,
    filter,
    setFilter,
    selected,
    selectCar,
    goBrowse,
    goDetail,
    goTheater,
    stageOptions,
    stageStripVisible,
    selectedStage,
    setStage: setStageIndex,
    stageSessionByDevice,
    selectedStageLabel,
    stageRoutes,
    driverSessions,
    handlePickSession,
    density,
    setDensity,
    lagFor,
    playerLagInfo,
    onPlayerEdge,
    modules,
    center,
    trailTrack: mapTrailTrack,
    trailCoverage: trail.coverage,
    playheadUtc,
    handleSeek,
    racelineCoords: raceline?.coords ?? null,
    stageProgress,
    stageLengthM: raceline?.totalLengthM ?? null,
    playerRef,
    handlePlayheadUtc,
    videoStartMs,
    handlePdtReady,
    lapOverride,
    initialSeekUtcMs,
    mountedSessionId,
    mountedPlayerKey,
    followedIds,
    toggleFollow,
    videoBadge,
    playerVod,
    glassRail,
    playbackSpeedKmh,
    laps: isCircuit ? laps : [],
    standings,
    selectedLapIdx,
    handlePickLap,
    handlePickLive,
    compareCapable,
    compareBusy,
    handleCompareOpen,
    compareRun,
    compareePlayerRef,
    handleCompareClose,
    stageEvents,
    mobilePanelOpen,
    setMobilePanelOpen,
  };

  return (
    <div className="flex h-[100dvh] w-full flex-col bg-bg text-fg">
      {showEventHeader && (
        <EventHeader
          event={event}
          watching={devices.length}
          onShare={handleShare}
          replay={replay}
          driverInline={
            mode === 'theater' && selected
              ? {
                  number: displayCarNumber(selected),
                  name: selected.driver_name || selected.device_id,
                }
              : null
          }
          // Desktop stage selector lives in the header (its dead middle
          // earns its keep, the map loses a floating pill, and theater
          // gains the strip for free). Phones keep the panel-skin strip
          // inside MobileViewer.
          centerSlot={
            stageStripVisible && !showLanding ? (
              <StageStrip
                options={stageOptions}
                selected={selectedStage}
                onChange={setStageIndex}
                skin="header"
                className="max-w-[46vw]"
              />
            ) : null
          }
        />
      )}
      {showLanding ? (
        <Landing
          event={event}
          variant={landingVariant}
          onBrowse={() => setLandingDismissed(true)}
          onPickCompetitor={() => {
            // CTA drops the fan into the Cars list: on phones that's the
            // panel overlay; on lg the browse rail already shows it.
            // goBrowse first — a stale/shared ?mode=detail&car=… URL
            // reaching the landing would otherwise render the Driver
            // tab instead of the Cars list (Codex review P2 on PR #74).
            goBrowse();
            setLandingDismissed(true);
            setMobilePanelOpen(true);
          }}
          // Countdown expiry: flip to the quiet variant (the window is
          // now open) instead of dumping the fan on an empty map.
          onExpired={() => setPreLiveExpired(true)}
        />
      ) : (
        <ViewerModeRouter c={controller} />
      )}
      <CompareRunPicker
        open={comparePickerOpen}
        runs={runs}
        enrollments={enrollments}
        excludeRunId={primaryRunId}
        busy={compareBusy}
        error={compareError}
        onPick={(r) => void handleComparePick(r)}
        onClose={() => {
          setComparePickerOpen(false);
          setCompareError(null);
        }}
      />
    </div>
  );
};

export default BrowseStateView;
